/** @jsxImportSource @emotion/react */
import React from 'react'
import { css } from '@emotion/react'
import colors from '@sportninja/common/constants/appColors'

/**
 * Checkbox icon - unchecked state (outline only)
 */
export const CheckboxOutlineIcon: React.FC<{ size?: number }> = ({
  size = 24,
}) => (
  <svg
    width={size}
    height={size}
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M19 5V19H5V5H19ZM19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3Z'
      fill={colors.NEUTRAL_50}
    />
  </svg>
)

/**
 * Checkbox icon - checked state (filled with checkmark)
 */
export const CheckboxCheckedIcon: React.FC<{ size?: number }> = ({
  size = 24,
}) => (
  <svg
    width={size}
    height={size}
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M19 3H5C3.89 3 3 3.9 3 5V19C3 20.1 3.89 21 5 21H19C20.11 21 21 20.1 21 19V5C21 3.9 20.11 3 19 3ZM10 17L5 12L6.41 10.59L10 14.17L17.59 6.58L19 8L10 17Z'
      fill={colors.PRIMARY_300}
    />
  </svg>
)

/**
 * Checkbox icon - disabled state (grayed out outline)
 */
export const CheckboxDisabledIcon: React.FC<{ size?: number }> = ({
  size = 24,
}) => (
  <svg
    width={size}
    height={size}
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M19 5V19H5V5H19ZM19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3Z'
      fill={colors.NEUTRAL_100}
    />
  </svg>
)

/**
 * Expand/Collapse arrow icon
 */
export const ExpandArrowIcon: React.FC<{ isExpanded: boolean }> = ({
  isExpanded,
}) => (
  <svg
    width='24'
    height='24'
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    css={css`
      transition: transform 0.2s ease;
      transform: ${isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'};
    `}
  >
    <path
      d='M7.41 8.59L12 13.17L16.59 8.59L18 10L12 16L6 10L7.41 8.59Z'
      fill={colors.NEUTRAL_50}
    />
  </svg>
)

/**
 * Chevron down icon (filled triangle)
 */
export const ChevronDown: React.FC<{ size?: number; color?: string }> = ({
  size = 16,
  color = colors.WHITE,
}) => (
  <svg
    width={size}
    height={size}
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path d='M7 10L12 15L17 10H7Z' fill={color} />
  </svg>
)

/**
 * Chevron right icon (filled triangle)
 */
export const ChevronRight: React.FC<{ size?: number; color?: string }> = ({
  size = 16,
  color = colors.WHITE,
}) => (
  <svg
    width={size}
    height={size}
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path d='M10 7L15 12L10 17V7Z' fill={color} />
  </svg>
)

/**
 * More vertical icon (3 dots)
 */
export const MoreVertIcon: React.FC<{ size?: number; color?: string }> = ({
  size = 16,
  color = colors.WHITE,
}) => (
  <svg
    width={size}
    height={size}
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z'
      fill={color}
    />
  </svg>
)
